import type { AuthResponseDto } from '@livestreamx/shared-types';

let accessToken: string | null = null;
// Ms epoch when accessToken stops being accepted. Zero = unknown/logged out.
// Kept so the realtime socket can proactively re-auth without reusing a token
// the server already rejected mid-handshake (that closes the WebSocket with
// "connection closed before receiving a handshake response").
let accessExpiresAt = 0;

export function setAccessToken(token: string | null, expiresInSeconds?: number): void {
  accessToken = token;
  if (expiresInSeconds) accessExpiresAt = Date.now() + expiresInSeconds * 1000;
  if (!token) accessExpiresAt = 0;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: string | string[],
  ) {
    super(message);
  }
}

/**
 * Invoked when the API answers 401 to a request that already tried (and
 * failed) to refresh — i.e. the session is dead server-side. The prime case
 * is a tenant suspension: the guard rejects every call for a suspended
 * tenant, so the SPA must drop its local session and land on /login instead
 * of showing an error page that keeps retrying.
 */
export type UnauthorizedHandler = (message: string) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

/** Endpoints where a 401 is a normal, expected outcome (bad password, stale cookie). */
function isAuthEndpoint(path: string): boolean {
  return (
    path.endsWith('/auth/login') ||
    path.endsWith('/auth/refresh') ||
    path.endsWith('/auth/register') ||
    path.endsWith('/auth/forgot-password') ||
    path.endsWith('/auth/reset-password')
  );
}

let refreshingPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshingPromise) return refreshingPromise;
  refreshingPromise = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { accessToken?: string | null; accessExpiresInSeconds?: number };
      accessToken = data.accessToken ?? null;
      if (data.accessExpiresInSeconds) accessExpiresAt = Date.now() + data.accessExpiresInSeconds * 1000;
      return accessToken;
    } catch {
      return null;
    } finally {
      refreshingPromise = null;
    }
  })();
  return refreshingPromise;
}

export async function ensureAccessToken(): Promise<string | null> {
  // Reuse the current token while it is definitely still valid (5s skew for
  // clock differences in the expiry check). Only hits /auth/refresh when it
  // has expired — this is what the realtime socket calls before each connect,
  // so a stale token never reaches the gateway handshake.
  if (accessToken && accessExpiresAt > Date.now() + 5000) return accessToken;
  return refreshAccessToken();
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

export async function api<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  let url = path;
  if (options.query) {
    const qs = Object.entries(options.query)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');
    if (qs) url = `${path}?${qs}`;
  }

  // The access token is memory-only and short-lived (JWT_ACCESS_TTL, 15m by
  // default). Refresh proactively when it is stale so an expired token never
  // reaches the API: reactive-only refresh guarantees a 401 round-trip (logged
  // in DevTools) at every token expiry. ensureAccessToken() is a no-op while
  // the current token is valid, and concurrent callers share one refresh.
  if (accessToken || hasSessionFlag()) await ensureAccessToken();

  const isForm = options.body instanceof FormData;
  const headers: Record<string, string> = {};
  if (!isForm) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const fetchOpts: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers,
    body: options.body === undefined ? undefined : (isForm ? (options.body as BodyInit) : JSON.stringify(options.body)),
    // Never use the HTTP cache for API calls: conditional requests can come
    // back as `304 Not Modified`, which has no readable body in fetch.
    cache: 'no-store',
  };

  let res = await fetch(url, fetchOpts);

  if (res.status === 401 && !path.endsWith('/auth/refresh')) {
    const token = await refreshAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      res = await fetch(url, { ...fetchOpts, headers });
    }
  }

  // Defensive: if a cached conditional response still yields 304, force a
  // full reload bypassing the HTTP cache entirely.
  if (res.status === 304) {
    res = await fetch(url, { ...fetchOpts, headers, cache: 'reload' });
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let details: string | string[] | undefined;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      message = Array.isArray(body.message) ? body.message.join(', ') : (body.message ?? message);
      details = body.message;
    } catch {
      /* non-JSON error body */
    }
    // 401 that survived a refresh attempt = session is dead server-side
    // (revoked, or the tenant got suspended). Hand it to the app-level
    // handler so it clears the session instead of leaving a zombie tab.
    if (res.status === 401 && !isAuthEndpoint(path) && hasSessionFlag()) {
      unauthorizedHandler?.(message);
    }
    throw new ApiError(res.status, message, details);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/* ---------- convenience helpers ---------- */

export const http = {
  get: <T>(path: string, query?: RequestOptions['query']) => api<T>(path, { query }),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => api<T>(path, { method: 'DELETE' }),
};

export type MeResponse = AuthResponseDto & { tenant: AuthResponseDto['tenant'] | null };

export async function fetchMe(): Promise<MeResponse | null> {
  // Skip the request entirely when there is provably no session (see the
  // `lsx_session` flag in store/auth.ts). An unauthenticated /auth/me would
  // 401 — and the retry inside api() would 401 again on /auth/refresh —
  // which only pollutes the console with "Failed to load resource" lines.
  if (!hasSessionFlag()) return null;
  try {
    // The access token is memory-only, so right after a page reload we hold a
    // session (flag + HttpOnly refresh cookie) but no token yet. Refresh first
    // so /auth/me never fires unauthenticated — otherwise every reload logs a
    // 401 (doubled by StrictMode's double-invoked effect) before the silent
    // refresh-and-retry inside api() recovers it.
    if (!accessToken) {
      const token = await refreshAccessToken();
      // Refresh cookie missing/expired → genuinely logged out.
      if (!token) return null;
    }
    const data = await api<MeResponse>('/api/auth/me');
    return data;
  } catch {
    return null;
  }
}

/**
 * True when a login happened in this browser and no logout cleared it.
 * The refresh token itself lives in an HttpOnly cookie (invisible to JS),
 * so this localStorage flag is our only client-side session hint.
 */
function hasSessionFlag(): boolean {
  try {
    return localStorage.getItem('lsx_session') === '1';
  } catch {
    return true;
  }
}